import type { Op } from 'quill/core';
import React, { useState } from 'react';
import classNames from 'classnames';
import { CREATION_ID, EditorActions } from '../../editor/editorSlice';
import AnnotationCreate from './AnnotationCreate';
import AnnotationLink from './AnnotationLink';
import AnnotationPreview from './AnnotationPreview';
import styles from './AnnotationSheet.module.scss';
import OverflowMenu from '../../components/OverflowMenu';
import { MenuItem } from '../../components/Menu';
import editIcon from '../../icons/edit.svg';
import MagneticWallSheet from '../MagneticWallSheet';
import useMagneticWallSheetContext from '../useMagneticWallSheetContext';
import useAnnotationLink from './useAnnotationLink';
import trashIcon from '../../icons/trash.svg';
import useDeleteAnnotation from './useDeleteAnnotation';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const ANNOTATION_FRAGMENT = gql(`
  fragment AnnotationSheet_annotation on PublicAnnotationFields {
    id
    ...AnnotationPreview_annotation
  }
`);

interface AnnotationSheetProps {
  postId: string;
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>;
  onClose: () => void;
  onInputChange: (content: Op[]) => void;
  unsavedInputValue: Op[];
}

const AnnotationSheet = ({
  postId,
  onClose,
  annotation,
  onInputChange,
  unsavedInputValue,
}: AnnotationSheetProps) => {
  const dispatch = useAppDispatch();

  const [isLinkInputOpen, setIsLinkInputOpen] = useState(false);

  const annotationLinkState = useAnnotationLink({
    id: annotation.id,
    initialValue: annotation.link ?? '',
    onUpdated() {
      setIsLinkInputOpen(false);
    },
  });

  const isNewAnnotation = annotation.id === CREATION_ID;
  const [isEditing, setIsEditing] = useState(isNewAnnotation);

  const focusAnnotationById = (id: string) => {
    dispatch(EditorActions.focusAnnotation({ id, isActive: true }));
  };

  const deleteAnnotation = useDeleteAnnotation();

  const onDelete = async () => {
    await deleteAnnotation(annotation.id);
    onClose();
  };

  const { highlighted } = useMagneticWallSheetContext({
    type: 'annotation',
    id: annotation.id,
  });

  return (
    <MagneticWallSheet
      title={isNewAnnotation ? 'New annotation' : 'Annotation'}
      onClose={onClose}
      headerContent={
        <>
          {highlighted && highlighted.length > 0 && (
            <div
              className={classNames(styles.contextContainer, {
                [styles.hasPreview]: !isEditing,
              })}
            >
              {highlighted}
            </div>
          )}
          {!isEditing && (
            <div className={styles.previewContainer}>
              <h5>Annotation</h5>
              <AnnotationPreview annotation={annotation} />
            </div>
          )}
          {!isEditing && annotation.link && (
            <AnnotationLink link={annotation.link} />
          )}
        </>
      }
      overflowMenu={
        !isEditing ? (
          <OverflowMenu>
            <MenuItem
              icon={editIcon}
              onClick={() => {
                setIsEditing(true);
                setIsLinkInputOpen(true);
              }}
            >
              Edit annotation
            </MenuItem>
            <MenuItem icon={trashIcon} danger onClick={onDelete}>
              Delete annotation
            </MenuItem>
          </OverflowMenu>
        ) : null
      }
      inputRow={
        isEditing ? (
          <>
            {!isLinkInputOpen && annotationLinkState.link && (
              <AnnotationLink link={annotationLinkState.link} />
            )}
            <div className={styles.createContainer}>
              <AnnotationCreate
                annotation={annotation}
                initialContent={
                  isEditing ? annotation.content ?? [] : unsavedInputValue
                }
                onChange={onInputChange}
                postId={postId}
                onSaved={(savedAnnotation) => {
                  focusAnnotationById(savedAnnotation.id);
                  onInputChange([]);
                  setIsEditing(false);
                }}
                annotationLinkState={annotationLinkState}
                isLinkInputOpen={isLinkInputOpen}
                setIsLinkInputOpen={setIsLinkInputOpen}
              />
            </div>
          </>
        ) : null
      }
    />
  );
};

export default AnnotationSheet;
